import { BookedSkatingPass } from '@types';
import { useAppSelector } from '_hooks';
import { UserService } from '_services';
import HelperMethods from '_utils/HelperMethods';
import React, { useCallback, useEffect, useState } from 'react';

interface AvailablePassesProps {
  selectedAdultPasses: BookedSkatingPass[];
  selectedChildPasses: BookedSkatingPass[];
  onSelectAdultPasses: (passes: BookedSkatingPass[]) => void;
  onSelectChildPasses: (passes: BookedSkatingPass[]) => void;
  maxAdultPasses: number;
  maxChildPasses: number;
  adultSkaterEmails?: string[]; // Array of adult skater emails to match against adult pass owner emails
  childSkaterEmails?: string[]; // Array of child skater emails to match against child pass owner emails
  sessionDate?: string; // Session calendar date (yyyy-MM-dd) used for Season Pass daily limit
}

export const AvailablePasses: React.FC<AvailablePassesProps> = ({
  selectedAdultPasses,
  selectedChildPasses,
  onSelectAdultPasses,
  onSelectChildPasses,
  maxAdultPasses,
  maxChildPasses,
  adultSkaterEmails = [],
  childSkaterEmails = [],
  sessionDate,
}) => {
  const [passes, setPasses] = useState<BookedSkatingPass[]>([]);
  const [passesLoading, setPassesLoading] = useState(true);

  // Get authentication state from Redux store
  const { isAuthenticated } = useAppSelector((state: any) => state.auth);

  const fetchPasses = useCallback(async () => {
    try {
      setPassesLoading(true);

      const responseData = (await UserService.getBookedSkatingPasses(
        sessionDate,
      )) as unknown as BookedSkatingPass[];

      setPasses(responseData);
    } catch (error) {
      console.error('Error fetching passes:', error);
      setPasses([]);
    } finally {
      setPassesLoading(false);
    }
  }, [sessionDate]);

  useEffect(() => {
    // Only fetch passes if user is authenticated
    if (isAuthenticated) {
      fetchPasses();
    } else {
      setPassesLoading(false);
    }
  }, [isAuthenticated, fetchPasses]);

  useEffect(() => {
    // Drop selections that are no longer redeemable for this session date
    if (selectedAdultPasses.length > 0) {
      const stillValid = selectedAdultPasses.filter(p => {
        const latest = passes.find(available => available.id === p.id) || p;
        return HelperMethods.canRedeemSkatingPass(latest);
      });
      if (stillValid.length !== selectedAdultPasses.length) {
        onSelectAdultPasses(stillValid);
      }
    }

    if (selectedChildPasses.length > 0) {
      const stillValid = selectedChildPasses.filter(p => {
        const latest = passes.find(available => available.id === p.id) || p;
        return HelperMethods.canRedeemSkatingPass(latest);
      });
      if (stillValid.length !== selectedChildPasses.length) {
        onSelectChildPasses(stillValid);
      }
    }
  }, [
    passes,
    onSelectAdultPasses,
    onSelectChildPasses,
    selectedAdultPasses,
    selectedChildPasses,
  ]);
  if (passesLoading) {
    return (
      <div className="flex items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-lg">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
          <p className="text-gray-600">Loading passes...</p>
        </div>
      </div>
    );
  }

  if (passes.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
        <div className="text-center">
          <div className="text-gray-400 mb-2">
            <svg
              className="w-12 h-12 mx-auto"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
              />
            </svg>
          </div>
          <h4 className="text-lg font-medium text-gray-900 mb-2">
            No Passes Found
          </h4>
          <p className="text-gray-500 text-sm">
            You don't have any skating passes available for this session.
          </p>
        </div>
      </div>
    );
  }

  // Helper function to check if a pass can be used by any of the skaters
  const canPassBeUsedBySkaters = (
    pass: BookedSkatingPass,
    passType: 'adult' | 'child',
  ): boolean => {
    // Get the appropriate email array based on pass type
    const relevantEmails =
      passType === 'adult' ? adultSkaterEmails : childSkaterEmails;

    // Passes require a matching skater email (pass holder only)
    if (!relevantEmails || relevantEmails.length === 0) {
      return false;
    }

    // If pass has no customer email, it can't be matched
    if (!pass.customerEmail) {
      return false;
    }

    // Check if any skater email matches the pass owner email
    return relevantEmails.some(email => {
      return (
        email.toLowerCase().trim() === pass.customerEmail.toLowerCase().trim()
      );
    });
  };

  const adultPasses = passes.filter(
    pass =>
      pass.selectedPassType?.toLowerCase() === 'adult' &&
      pass.isActive &&
      canPassBeUsedBySkaters(pass, 'adult'),
  );

  const childPasses = passes.filter(
    pass =>
      pass.selectedPassType?.toLowerCase() === 'child' &&
      pass.isActive &&
      canPassBeUsedBySkaters(pass, 'child'),
  );

  const renderPassCard = (
    pass: BookedSkatingPass,
    isSelected: boolean,
    onToggle: (pass: BookedSkatingPass) => void,
    _passType: 'adult' | 'child',
    isDisabled: boolean = false,
  ) => {
    const effectiveMaxUses = HelperMethods.getEffectiveMaxUses(pass);
    const isFullyUsed =
      effectiveMaxUses != null &&
      Number(pass.redeemedCount || 0) >= effectiveMaxUses;
    const canUse = HelperMethods.canRedeemSkatingPass(pass) && !isDisabled;

    return (
      <div
        key={pass.id}
        className={`relative border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
          isSelected
            ? 'border-blue-500 bg-blue-50 shadow-md'
            : canUse
              ? 'border-gray-200 hover:border-blue-300 hover:shadow-sm'
              : 'border-gray-100 bg-gray-50 cursor-not-allowed opacity-60'
        }`}
        onClick={() => canUse && onToggle(pass)}
      >
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2">
              <div
                className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                  isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                }`}
              >
                {isSelected && (
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                )}
              </div>
              <h6 className="font-medium text-gray-900">{pass.passName}</h6>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              <div className="flex items-center space-x-4">
                <span>
                  Used: {pass.redeemedCount} / {effectiveMaxUses ?? '∞'}
                </span>
                <span
                  className={`px-2 py-1 rounded-full text-xs font-medium ${
                    isFullyUsed
                      ? 'bg-red-100 text-red-800'
                      : canUse
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-600'
                  }`}
                >
                  {isFullyUsed ? 'Fully Used' : canUse ? 'Available' : ''}
                </span>
              </div>
              {(pass.customerName || pass.customerEmail) && (
                <div className="mt-2 text-xs text-gray-500">
                  {pass.customerName && (
                    <div className="flex items-center space-x-1">
                      <span className="font-medium">Owner:</span>
                      <span>{pass.customerName}</span>
                    </div>
                  )}
                  {pass.customerEmail && (
                    <div className="flex items-center space-x-1">
                      <span className="font-medium">Email:</span>
                      <span>{pass.customerEmail}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          {isSelected && (
            <div className="ml-4">
              <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                <div className="w-3 h-3 bg-white rounded-full"></div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6">
      <div className="flex items-center space-x-2 mb-4">
        <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
        <h4 className="text-lg font-semibold text-gray-900">
          Available Passes
        </h4>
      </div>

      <div className="space-y-4">
        {/* Adult Passes - Only show if there are adult skater emails */}
        {adultPasses.length > 0 && adultSkaterEmails.length > 0 && (
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-3">
              Adult Passes ({selectedAdultPasses.length} of {maxAdultPasses}{' '}
              selected)
            </h5>
            <div className="grid gap-3">
              {adultPasses.map(pass => {
                const isSelected = selectedAdultPasses.some(
                  p => p.id === pass.id,
                );

                // Check if we can select more passes
                const canSelectMore =
                  selectedAdultPasses.length < maxAdultPasses;

                const hasRemainingUses =
                  HelperMethods.canRedeemSkatingPass(pass);

                const handleToggle = (togglePass: BookedSkatingPass) => {
                  if (isSelected) {
                    // Deselect this pass - remove it from the array
                    onSelectAdultPasses(
                      selectedAdultPasses.filter(p => p.id !== togglePass.id),
                    );
                  } else if (canSelectMore && hasRemainingUses) {
                    // Select this pass - add it to the array
                    onSelectAdultPasses([...selectedAdultPasses, togglePass]);
                  }
                };

                const isDisabled =
                  !isSelected && (!canSelectMore || !hasRemainingUses);

                return renderPassCard(
                  pass,
                  isSelected,
                  handleToggle,
                  'adult',
                  isDisabled,
                );
              })}
            </div>
          </div>
        )}

        {/* Child Passes - Only show if there are child skater emails */}
        {childPasses.length > 0 && childSkaterEmails.length > 0 && (
          <div>
            <h5 className="text-sm font-medium text-gray-700 mb-3">
              Child Passes ({selectedChildPasses.length} of {maxChildPasses}{' '}
              selected)
            </h5>
            <div className="grid gap-3">
              {childPasses.map(pass => {
                const isSelected = selectedChildPasses.some(
                  p => p.id === pass.id,
                );

                // Check if we can select more passes
                const canSelectMore =
                  selectedChildPasses.length < maxChildPasses;

                const hasRemainingUses =
                  HelperMethods.canRedeemSkatingPass(pass);

                const handleToggle = (togglePass: BookedSkatingPass) => {
                  if (isSelected) {
                    // Deselect this pass - remove it from the array
                    onSelectChildPasses(
                      selectedChildPasses.filter(p => p.id !== togglePass.id),
                    );
                  } else if (canSelectMore && hasRemainingUses) {
                    // Select this pass - add it to the array
                    onSelectChildPasses([...selectedChildPasses, togglePass]);
                  }
                };

                const isDisabled =
                  !isSelected && (!canSelectMore || !hasRemainingUses);

                return renderPassCard(
                  pass,
                  isSelected,
                  handleToggle,
                  'child',
                  isDisabled,
                );
              })}
            </div>
          </div>
        )}

        {/* No active passes available message */}
        {(adultPasses.length === 0 || adultSkaterEmails.length === 0) &&
          (childPasses.length === 0 || childSkaterEmails.length === 0) && (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <svg
                  className="w-12 h-12 mx-auto"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
                  />
                </svg>
              </div>
              <p className="text-gray-500 text-sm">
                No active passes available
              </p>
            </div>
          )}
      </div>
    </div>
  );
};
